import { RELIABILITY_LABELS, type ReliabilityStatus } from '../reliability';
import { PRINT_COLORS } from '../snapshots';
import { FIGURE_WIDTH, figureTitle, footLines, n1, svgFrame, svgLabel, textWidth, wrapText } from './svg';

// A timeline with a reliability-status badge per event: the events in date order down a vertical axis, each with
// its date, its label and a badge (Official document / Corroborated / Single source / Hypothesis) drawn from the
// claims behind it. Vertical, so long labels and many events stay readable on an A4 page. Pure and deterministic.

export const STATUS_TIMELINE_MAX_EVENTS = 40;
export const STATUS_TIMELINE_LABEL_CHARS = 160;

export interface StatusTimelineEvent {
  /** ISO prefix (YYYY, YYYY-MM, YYYY-MM-DD) or the date as written; events sort by this text. */
  date: string;
  label: string;
  status: ReliabilityStatus;
  /** Color slot (1–12) of the event's mark; null = neutral. */
  slot?: number | null;
}

export interface StatusTimelineSvgInput {
  title: string;
  events: readonly StatusTimelineEvent[];
  language?: 'en' | 'fr';
  source?: string | null;
}

const PAD = 20;
const TOP = 44;
const ROW_H = 34;
const DATE_W = 86;
const AXIS_X = PAD + DATE_W + 12;
const BADGE_H = 16;

/** The badge's fill per status: strongest darkest. */
const BADGE_FILL: Record<ReliabilityStatus, string> = {
  official_document: PRINT_COLORS.foreground,
  corroborated: PRINT_COLORS.entity(1),
  single_source: PRINT_COLORS.line,
  hypothesis: PRINT_COLORS.background,
};

/** Events in date order (text order of the ISO prefix), stable for equal dates. */
export const sortTimelineEvents = (events: readonly StatusTimelineEvent[]): StatusTimelineEvent[] =>
  events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => a.e.date.localeCompare(b.e.date) || a.i - b.i)
    .map(x => x.e);

export function statusTimelineSvg(input: StatusTimelineSvgInput): { svg: string; width: number; height: number } {
  const width = FIGURE_WIDTH;
  const lang = input.language ?? 'en';
  const labels = RELIABILITY_LABELS[lang];
  const events = sortTimelineEvents(input.events).slice(0, STATUS_TIMELINE_MAX_EVENTS);
  if (!events.length) {
    const h = TOP + 40;
    return {
      svg: svgFrame(
        width,
        h,
        input.title,
        figureTitle(input.title) + svgLabel('Nothing dated to draw.', PAD, TOP + 16, { fill: PRINT_COLORS.muted }),
      ),
      width,
      height: h,
    };
  }
  const badgeW = (s: ReliabilityStatus) => Math.round(textWidth(labels[s], 9) + 14);
  const widest = Math.max(...events.map(e => badgeW(e.status)));
  const labelX = AXIS_X + 18;
  const labelMax = Math.max(20, Math.floor((width - PAD - widest - 12 - labelX) / 6.2));
  const bottom = TOP + events.length * ROW_H;
  const parts: string[] = [
    figureTitle(input.title),
    `<line x1="${AXIS_X}" y1="${TOP - 6}" x2="${AXIS_X}" y2="${bottom - ROW_H / 2}" stroke="${PRINT_COLORS.line}" stroke-width="1.5"/>`,
  ];
  events.forEach((e, i) => {
    const cy = TOP + i * ROW_H + ROW_H / 2 - 6;
    const bx = width - PAD - badgeW(e.status);
    const dark = e.status === 'official_document' || e.status === 'corroborated';
    parts.push(
      svgLabel(e.date, AXIS_X - 12, cy, { size: 10, fill: PRINT_COLORS.muted, max: 14, align: 'left' }),
      `<circle cx="${AXIS_X}" cy="${n1(cy)}" r="5" fill="${PRINT_COLORS.entity(e.slot ?? null)}" stroke="${PRINT_COLORS.foreground}" stroke-opacity="0.55" stroke-width="1"/>`,
      svgLabel(e.label, labelX, cy, { size: 11, max: labelMax }),
      `<rect x="${n1(bx)}" y="${n1(cy - BADGE_H / 2)}" width="${badgeW(e.status)}" height="${BADGE_H}" rx="8" fill="${BADGE_FILL[e.status]}" stroke="${PRINT_COLORS.foreground}" stroke-opacity="${dark ? 0 : 0.55}" stroke-width="1" data-status="${e.status}"/>`,
      svgLabel(labels[e.status], bx + badgeW(e.status) / 2, cy, {
        size: 9,
        weight: 600,
        fill: dark ? PRINT_COLORS.background : PRINT_COLORS.foreground,
        align: 'center',
        max: 24,
      }),
    );
  });
  // Legend of the four statuses under the axis.
  const ly = bottom + 10;
  let lx = PAD;
  for (const s of ['official_document', 'corroborated', 'single_source', 'hypothesis'] as const) {
    const w = badgeW(s);
    const dark = s === 'official_document' || s === 'corroborated';
    parts.push(
      `<rect x="${lx}" y="${ly - BADGE_H / 2}" width="${w}" height="${BADGE_H}" rx="8" fill="${BADGE_FILL[s]}" stroke="${PRINT_COLORS.foreground}" stroke-opacity="${dark ? 0 : 0.55}" stroke-width="1"/>`,
      svgLabel(labels[s], lx + w / 2, ly, {
        size: 9,
        weight: 600,
        fill: dark ? PRINT_COLORS.background : PRINT_COLORS.foreground,
        align: 'center',
        max: 24,
      }),
    );
    lx += w + 10;
  }
  const foot = input.source ? footLines(wrapText(input.source, 135, 2), ly + 24) : null;
  const height = Math.round(ly + 16 + (foot ? foot.height + 8 : 0));
  if (foot) parts.push(foot.svg);
  const desc = events.map(e => `${e.date}: ${e.label} (${labels[e.status]})`).join('; ');
  return { svg: svgFrame(width, height, `${input.title}. ${desc}`, parts.join('\n')), width, height };
}

/** The events as a table (date, event, status), in the figure's order. */
export function statusTimelineTable(
  input: Pick<StatusTimelineSvgInput, 'events' | 'language'>,
  headers: readonly [string, string, string],
): { headers: string[]; rows: string[][] } {
  const labels = RELIABILITY_LABELS[input.language ?? 'en'];
  return {
    headers: [...headers],
    rows: sortTimelineEvents(input.events)
      .slice(0, STATUS_TIMELINE_MAX_EVENTS)
      .map(e => [e.date, e.label, labels[e.status]]),
  };
}
